import React, { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { retrieveLaunchParams } from '@telegram-apps/sdk-react';
import { AuthProvider } from './contexts/AuthContext';
import { EnergyProvider } from './contexts/EnergyContext';
import { GameDataProvider } from './contexts/GameDataContext';
import './styles/index.css';
import App from './App.jsx';
import './i18n';
import { queryClient } from './api/apiClient';
import { store } from './redux/store';
import { init } from './utils/init';
import { BookmarksProvider } from './contexts/BookmarksContext.jsx';
import { VotesProvider } from './contexts/VotesContext.jsx';
import './utils/logger'; // Инициализируем систему логирования
import { initErrorReporter } from './utils/errorReporter'; // Мониторинг ошибок
import { initRemoteLogger } from './utils/remoteLogger'; // Remote debugging

// Инициализируем отправку ошибок на сервер (production/test only)
initErrorReporter();

// Инициализируем remote logger для отладки (отправляет console.log на сервер)
initRemoteLogger();

// Версия билда для отслеживания деплоя
console.log('📦 Build:', typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'dev');

// ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ ДЛЯ ОТСЛЕЖИВАНИЯ СОЗДАНИЯ TELEGRAM WEBAPP
console.log('🔍 main.jsx: Проверяем window.Telegram ДО инициализации...');
console.log('🔍 window.Telegram:', window.Telegram);
console.log('🔍 window.Telegram?.WebApp:', window.Telegram?.WebApp);
console.log('🔍 window.Telegram?.WebApp?.initData:', window.Telegram?.WebApp?.initData);
console.log('🔍 window.Telegram?.WebApp?.initDataUnsafe:', window.Telegram?.WebApp?.initDataUnsafe);

let isTelegram = !!(window.Telegram?.WebApp);
let isRealTelegram = false; // Флаг для настоящего Telegram WebApp
let isSDKInitialized = false; // Флаг для предотвращения повторной инициализации

// Проверяем, является ли это настоящим Telegram WebApp
if (isTelegram) {
    const webApp = window.Telegram.WebApp;
    const hasValidInitData = webApp.initData && webApp.initData.length > 0;
    const hasValidUser = webApp.initDataUnsafe?.user?.id;
    
    // Настоящий Telegram WebApp должен иметь валидный initData и пользователя
    isRealTelegram = hasValidInitData && hasValidUser;
    
    console.log('🔍 Telegram WebApp анализ:', {
        hasWebApp: true,
        hasInitData: !!hasValidInitData,
        hasValidUser: !!hasValidUser,
        isRealTelegram,
        initDataLength: webApp.initData?.length || 0,
        userId: webApp.initDataUnsafe?.user?.id
    });
} else {
    console.log('⚠️ Запущено вне Telegram – SDK не инициализируется');
}

// Добавляем глобальное логирование для отладки
console.log('🚀 main.jsx: Начинаем загрузку приложения...');
console.log('📱 User Agent:', navigator.userAgent);
console.log('🌐 URL:', window.location.href);
console.log('🔗 Referrer:', document.referrer);

// Проверяем Telegram WebApp
if (window.Telegram) {
    console.log('✅ Telegram объект найден');
    if (window.Telegram.WebApp) {
        console.log('✅ Telegram WebApp найден');
        console.log('📊 InitData:', window.Telegram.WebApp.initData);
        console.log('👤 User:', window.Telegram.WebApp.initDataUnsafe?.user);
    } else {
        console.warn('⚠️ Telegram WebApp не найден');
    }
} else {
    console.warn('⚠️ Telegram объект не найден');
}

function Root() {
    console.log('🔧 Root: Компонент создается...');
    

    // Обработчик ошибок
    useEffect(() => {
        console.log('🔧 Root: Настраиваем обработчики ошибок...');
        const handleError = (error) => {
            console.error('❌ Global error:', error);
            // В production не показываем alert
            if (process.env.NODE_ENV === 'development') {
                alert(`Ошибка приложения: ${error.message}`);
            }
        };

        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', (event) => {
            console.error('❌ Unhandled promise rejection:', event.reason);
            if (process.env.NODE_ENV === 'development') {
                alert(`Необработанная ошибка: ${event.reason}`);
            }
        });

        return () => {
            window.removeEventListener('error', handleError);
        };
    }, []);

    console.log('🔧 Root: Рендерим компоненты...');
    
    // Динамически определяем basename в зависимости от URL
    const currentPath = window.location.pathname;
    const basename = currentPath.startsWith('/telegram') ? '/telegram' : '/';
    
    console.log('🔧 Root: Определен basename:', basename, 'для пути:', currentPath);
    
    // В настоящем Telegram WebApp не используем StrictMode для избежания двойной инициализации
    const appContent = (
        <BrowserRouter basename={basename}>
            <Provider store={store}>
                <QueryClientProvider client={queryClient}>
                    <AuthProvider>
                        <GameDataProvider>
                            <EnergyProvider>
                                <BookmarksProvider>
                                    <VotesProvider>
                                        <App />
                                    </VotesProvider>
                                </BookmarksProvider>
                            </EnergyProvider>
                        </GameDataProvider>
                    </AuthProvider>
                </QueryClientProvider>
            </Provider>
        </BrowserRouter>
    );
    
    return isRealTelegram ? appContent : <StrictMode>{appContent}</StrictMode>;
}

// Инициализация корня приложения
console.log('🔧 main.jsx: Инициализируем React root...');
const rootElement = document.getElementById('root');

if (!rootElement) {
    console.error('❌ Root element не найден!');
    throw new Error('Root element не найден');
}

console.log('✅ Root element найден, создаем React root...');
const root = createRoot(rootElement);

// Инициализация Telegram WebApp SDK (только для настоящего Telegram)
if (isRealTelegram && !isSDKInitialized) {
    try {
        const debug = retrieveLaunchParams().startParam === 'debug' || import.meta.env.DEV;
        console.log('🔧 Инициализируем Telegram WebApp SDK для НАСТОЯЩЕГО Telegram, debug:', debug);
        init(debug);
        isSDKInitialized = true;
        console.log('✅ Telegram WebApp SDK инициализирован');
    } catch (e) {
        console.log('❌ Ошибка инициализации Telegram WebApp SDK:', e);
    }
} else if (isSDKInitialized) {
    console.log('⚠️ Telegram WebApp SDK уже инициализирован, пропускаем');
} else if (isTelegram && !isRealTelegram) {
    console.log('⚠️ Обнаружен фейковый Telegram WebApp - SDK НЕ инициалируем');
} else {
    console.log('⚠️ Telegram WebApp не обнаружен, SDK не инициализируем');
}

// Функция для скрытия прелоадера (ВРЕМЕННО ОТКЛЮЧЕНА ДЛЯ ИССЛЕДОВАНИЯ)
function hidePreloader() {
    const preloader = document.getElementById('app-preloader');
    if (preloader) {
        console.log('🎨 Скрываем прелоадер...');
        // Мгновенно скрываем прелоадер
        preloader.style.display = 'none';
        // Удаляем из DOM для очистки памяти
        preloader.remove();
        console.log('✅ Прелоадер удален');
    }
}

// Минимальное время показа прелоадера (в миллисекундах)
const MIN_PRELOADER_TIME = 2000; // 2 секунды
const startTime = Date.now();

// Теперь вызываем render только один раз
console.log('🚀 Запускаем рендер приложения...');
root.render(<Root />);
console.log('✅ Приложение запущено!');

// Скрываем прелоадер с учетом минимального времени показа
setTimeout(() => {
    const elapsedTime = Date.now() - startTime;
    const remainingTime = Math.max(0, MIN_PRELOADER_TIME - elapsedTime);
    
    console.log(`⏱️ Прошло времени: ${elapsedTime}ms, осталось показать: ${remainingTime}ms`);
    
    setTimeout(hidePreloader, remainingTime);
}, 300); // Даем React время отрендерить первый кадр
